import React from 'react';

/**
 * Continuously scrolling testimonial rail.
 *
 * The children are rendered twice back-to-back and the track is translated by
 * exactly -50%, so the second copy lands where the first began and the loop is
 * seamless — no jump, no snap-back. Duration scales with the number of items so
 * a long list doesn't race past; speed stays roughly constant per card.
 *
 * Hovering pauses the rail (CSS `animation-play-state`), and the whole thing
 * falls back to a plain horizontal scroller when the visitor has asked for
 * reduced motion — see `.ss-marquee` in styles/motion.css.
 */
export default function TestimonialMarquee({
  items,
  renderItem,
  itemKey = (_, i) => i,
  secondsPerItem = 7,
  cardClassName = 'w-[86vw] sm:w-[420px] shrink-0 flex',
  gapClassName = 'gap-6',
  className = '',
}) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;

  const duration = Math.max(18, list.length * secondsPerItem);

  const row = (copy) =>
    list.map((item, i) => (
      <div
        key={`${copy}-${itemKey(item, i)}`}
        className={cardClassName}
        // The duplicated run is decorative: screen readers read the first copy only.
        aria-hidden={copy === 1 ? 'true' : undefined}
      >
        {renderItem(item, i)}
      </div>
    ));

  return (
    <div className={`ss-marquee ${className}`}>
      <div
        className={`ss-marquee-track flex ${gapClassName}`}
        style={{ '--ss-marquee-duration': `${duration}s` }}
      >
        {/* Padding-right on the first run keeps the gap even across the seam. */}
        <div className={`flex ${gapClassName} shrink-0 pr-6`}>{row(0)}</div>
        <div className={`flex ${gapClassName} shrink-0 pr-6`}>{row(1)}</div>
      </div>
    </div>
  );
}
